import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    question: "What is No Win, No Fee**? Can I claim back myself?",
    answer: (
      <>
        <p>Our service is provided on a &lsquo;No win, No fee&rsquo; basis. This means that you only pay our fees in the event that your claim is successful. You can cancel, without charge, within the 14-day cooling-off period, after that we may charge for our reasonable costs incurred, which is calculated on an hourly basis subject to a maximum charge.</p>
        <p>For claims that settle without litigation (e.g. through the FCA&rsquo;s Consumer Redress Scheme), our fees are charged in line with our regulator&rsquo;s fee cap rules, please see below:</p>
        <div className="fee-table-wrapper">
          <table className="fee-table">
            <thead>
              <tr><th>Band</th><th>Redress (&pound;)</th><th>Received % Fee</th><th>Maximum Fee</th></tr>
            </thead>
            <tbody>
              <tr><td>A</td><td>&pound;1-1,499</td><td>30%</td><td>&pound;420</td></tr>
              <tr><td>B</td><td>&pound;1,500-9,999</td><td>28%</td><td>&pound;2,500</td></tr>
              <tr><td>C</td><td>&pound;10,000-24,999</td><td>25%</td><td>&pound;5,000</td></tr>
              <tr><td>D</td><td>&pound;25,000-49,999</td><td>20%</td><td>&pound;7,500</td></tr>
              <tr><td>E</td><td>&pound;50,000+</td><td>15%</td><td>&pound;10,000</td></tr>
            </tbody>
          </table>
        </div>
        <p>The fees listed above are exclusive of VAT and will be subject to VAT at the prevailing rate.</p>
        <p>The FCA has stated that it believes that the average claim value, per eligible agreement, will be around &pound;830. However, some claims will be more and others will be less. We therefore anticipate that the majority of claims will be between &pound;500 and &pound;1,250, however in some circumstances, you may be entitled to more if your claim is pursued through litigation.</p>
        <p>If your claim proceeds to litigation, our fees may be charged on a different basis (e.g. hourly rates and a success fee). We would only recommend litigation where we believe it will achieve a better outcome than the FCA&rsquo;s Consumer Redress Scheme (e.g. a higher settlement).</p>
        <p>Some costs may be recoverable from the defendant and, in all cases, any deduction from your compensation will not exceed 40% + VAT. If the claim is unsuccessful, you will not owe our fees, but you may be liable for adverse costs (including the defendant&rsquo;s legal costs and disbursements). These risks can be reduced by taking out After the Event (ATE) insurance, which we would usually recommend and can help arrange for you.</p>
        <p>Please refer to our <Link to="/terms-of-engagement">Terms of Engagement</Link> for full details regarding all of our charges.</p>
        <p><strong>Alternative Options</strong></p>
        <p>You are also free to:</p>
        <ul>
          <li>Use your own legal representation to pursue a court claim, or</li>
          <li>Directly complain to your lender at no cost. If dissatisfied, you can escalate your complaint to the Financial Ombudsman Service, which is also free of charge</li>
          <li>The FCA has now introduced a Consumer Redress Scheme.</li>
        </ul>
      </>
    ),
  },
  {
    question: "What is mis-sold car finance?",
    answer: (
      <p>Mis-sold car finance typically relates to Discretionary Commission Arrangements (DCAs). This is where the car dealer could set the interest rate on your finance agreement, and the higher the rate they set, the more commission they earned. This meant many consumers paid more for their car finance than they should have, without being told about this conflict of interest. The FCA banned DCAs in January 2021. However, the FCA has also expressed concerns about high fixed undisclosed commissions and the non-disclosure of certain other facts that could create an unfair relationship. Therefore, consumers affected between April 2007 and October 2024 may be entitled to compensation.</p>
    ),
  },
  {
    question: "How much does this cost?",
    answer: (
      <>
        <p>We operate on a No Win, No Fee** basis. This means you will not pay anything upfront, and if your claim is unsuccessful, you will not owe us a penny. If we win your claim, our success fee is a maximum of 36% (inclusive of VAT) of the compensation recovered where the claim settles without litigation (e.g. through the FCA&rsquo;s Consumer Redress Scheme). Where claims are settled through litigation, the maximum amount that would be deducted is 48% inclusive of VAT.</p>
        <p>You have a 14-day cooling-off period during which you can cancel without charge. For cancellations after the 14-day cooling period, we may charge a fee for our reasonable costs incurred, subject to a maximum charge. Please refer to our <Link to="/terms-of-engagement">Terms of Engagement</Link> for full details on charges.</p>
      </>
    ),
  },
  {
    question: "How much is the average claim?",
    answer: (
      <p>The Financial Conduct Authority (FCA) has indicated that under its Consumer Redress Scheme, eligible claims could be an average of around &pound;830* per finance agreement. However, actual compensation varies depending on factors such as the interest rate charged, the length of your agreement, and the total amount financed. The compensation scheme for mis-sold car finance is expected to cost lenders up to &pound;7.5 billion in total.</p>
    ),
  },
  {
    question: "Can I make more than one claim?",
    answer: (
      <p>Yes! You can make one claim per finance agreement. So if, for example, you took out four cars on finance between April 2007 and October 2024, then you could be eligible for four separate claims. Our online tool can help you find ALL of your car finance agreements from this period.</p>
    ),
  },
  {
    question: "What if I do not have my paperwork?",
    answer: (
      <p>Do not worry - you do not need to have any paperwork to get started. We can help you find your finance agreements and request the necessary documentation directly from the lender on your behalf. All you need to begin is your basic personal details.</p>
    ),
  },
  {
    question: "Will making a claim affect my credit score?",
    answer: (
      <p>No, making a claim will not affect your credit score. The claim is against the lender for hidden commission, not a dispute about your ability to repay the loan. Your credit history and score remain completely unaffected throughout the claims process.</p>
    ),
  },
  {
    question: "Can I claim if I have already paid off my car finance?",
    answer: (
      <p>Yes, absolutely! You can still make a claim even if you have fully paid off your car finance. As long as your finance agreement was active between April 2007 and October 2024, you may be entitled to compensation for hidden commission charges.</p>
    ),
  },
  {
    question: "Can I claim back myself?",
    answer: (
      <p>Yes, you do not have to use a law firm or claims management company to make your claim. You are entitled to pursue it directly at no cost by complaining to your lender. If you are dissatisfied with the lenders response, you can escalate the matter to the Financial Ombudsman Service at no cost. However, many people prefer to use our expertise to navigate the process and maximise their compensation.</p>
    ),
  },
];

export default function FAQSection() {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggle = (index) => {
    setActiveIndex(prev => prev === index ? null : index);
  };

  return (
    <section className="faq-section" data-testid="faq-section">
      <h2>Frequently Asked Questions</h2>
      <div className="faq-list">
        {faqs.map((faq, i) => {
          const isOpen = activeIndex === i;
          return (
            <div
              key={faq.question}
              className={`faq-item${isOpen ? " active" : ""}`}
              data-testid={`faq-item-${i}`}
            >
              <button
                className="faq-question"
                onClick={() => toggle(i)}
                data-testid={`faq-question-${i}`}
                type="button"
              >
                <span>{faq.question}</span>
                <ChevronDown
                  size={20}
                  className={`faq-chevron${isOpen ? " open" : ""}`}
                />
              </button>
              <div className={`faq-answer-wrapper${isOpen ? " open" : ""}`}>
                <div className="faq-answer">
                  <div className="faq-answer-inner">{faq.answer}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

